<template>
  <article 
    class="news-item" 
    :class="{ 'compact-mode': isCompact }"
  >
    <a 
      :href="item.link" 
      target="_blank" 
      rel="noopener noreferrer" 
      class="news-link"
      :title="`Öppna artikel hos ${item.sourceName}: ${item.title}`"
    >
      <div class="meta-row">
        <span 
          class="source-badge" 
          :style="{ backgroundColor: item.badgeColor, color: item.textColor }"
        >
          {{ item.sourceShort || item.sourceName }}
        </span>
        <span v-if="item.category" class="category-pill">
          {{ item.category }}
        </span>
        <span class="timestamp" :title="formattedFullDate">
          {{ item.timeAgo || 'Nyligen' }}
        </span>
      </div>

      <div class="headline-row">
        <h2 class="title">{{ item.title }}</h2>
        <span class="external-icon" aria-hidden="true">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="7" y1="17" x2="17" y2="7"></line>
            <polyline points="7 7 17 7 17 17"></polyline>
          </svg>
        </span>
      </div>
    </a>
  </article>
</template>

<script>
export default {
  name: 'NewsItem',
  props: {
    item: {
      type: Object,
      required: true
    },
    isCompact: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    formattedFullDate() {
      if (!this.item.pubDate) return ''
      try {
        const d = new Date(this.item.pubDate)
        return d.toLocaleString('sv-SE', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch {
        return ''
      }
    }
  }
}
</script>

<style scoped>
.news-item {
  border-bottom: 1px solid var(--border-color);
  transition: background-color 0.15s ease;
}

.news-item:hover {
  background-color: var(--item-hover-bg);
}

.news-link {
  display: block;
  padding: 18px 20px;
  text-decoration: none;
  color: inherit;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}

.news-link:focus-visible {
  outline: 2px solid var(--accent-color);
  outline-offset: -2px;
}

.meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.source-badge {
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  padding: 2px 7px;
  border-radius: 3px;
  text-transform: uppercase;
  display: inline-block;
  line-height: 1.3;
}

.category-pill {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 1px 6px;
  border-radius: 3px;
  background-color: var(--pill-bg);
  color: var(--text-muted);
  border: 1px solid var(--border-color);
  white-space: nowrap;
  line-height: 1.3;
}

.timestamp {
  font-size: 0.78rem;
  color: var(--text-muted);
  font-weight: 500;
}

.headline-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}

.title {
  margin: 0;
  font-size: 1.15rem;
  line-height: 1.38;
  font-weight: 700;
  color: var(--text-primary);
  font-family: 'Newsreader', Georgia, serif;
  letter-spacing: -0.01em;
  flex: 1;
  transition: color 0.15s ease;
}

.news-item:hover .title {
  color: var(--accent-hover);
}

.external-icon {
  flex-shrink: 0;
  margin-top: 3px;
  color: var(--text-muted);
  opacity: 0.6;
  transition: transform 0.2s ease, opacity 0.2s ease, color 0.2s ease;
}

.news-item:hover .external-icon {
  opacity: 1;
  color: var(--accent-hover);
  transform: translate(2px, -2px);
}

/* Kompakt visningsläge */
.compact-mode .news-link {
  padding: 12px 16px;
}

.compact-mode .title {
  font-size: 1.02rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 600;
}

.compact-mode .meta-row {
  margin-bottom: 4px;
}

@media (max-width: 600px) {
  .news-link {
    padding: 14px 16px;
  }
  .title {
    font-size: 1.06rem;
    line-height: 1.35;
  }
  .meta-row {
    gap: 6px;
  }
}
</style>
